import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export interface AdminConsumerUser {
  id: string
  nickname?: string
  phone?: string
  roles?: string[]
  permissions?: string[]
  hasOrderManagePermission?: boolean
  level?: string
  levelName?: string
  points?: number
  growth?: number
  balance?: number
  frozen?: number
  orders?: number
  amount?: number
  last?: string
  status?: string
  createdAt?: string
  updatedAt?: string
}

export interface AdminWalletLog {
  id: string
  userId: string
  nickname?: string
  changeAmount?: number
  balanceAfter?: number
  type?: string
  bizType?: string
  bizId?: string
  remark?: string
  createdAt?: string
}

export interface AdminPointsLog {
  id: string
  userId: string
  nickname?: string
  changePoints?: number
  pointsAfter?: number
  type?: string
  bizType?: string
  bizId?: string
  remark?: string
  createdAt?: string
}

export interface AdminUsersParams {
  page?: number
  pageSize?: number
  keyword?: string
  level?: string
  adminPermission?: string
}

export interface UserBalancePayload {
  amount: number
  remark?: string
}

export interface UserPointsPayload {
  points: number
  remark?: string
}

export interface UserStatusPayload {
  status: string
}

export interface UserAdminPermissionPayload {
  enabled: boolean
  roles?: string[]
  permissions?: string[]
}

export const userApiReady = true as const

export const fetchAdminUsers = async (params: AdminUsersParams): Promise<PageResult<AdminConsumerUser>> =>
  request<PageResult<AdminConsumerUser>>({
    url: '/users',
    method: 'GET',
    params,
  })

export const fetchAdminUser = async (id: string): Promise<AdminConsumerUser> =>
  request<AdminConsumerUser>({
    url: `/users/${id}`,
    method: 'GET',
  })

export const adjustUserBalance = async (id: string, payload: UserBalancePayload): Promise<void> =>
  request<void, UserBalancePayload>({
    url: `/users/${id}/balance`,
    method: 'PUT',
    data: payload,
  })

export const adjustUserPoints = async (id: string, payload: UserPointsPayload): Promise<void> =>
  request<void, UserPointsPayload>({
    url: `/users/${id}/points`,
    method: 'PUT',
    data: payload,
  })

export const updateAdminUserStatus = async (id: string, payload: UserStatusPayload): Promise<void> =>
  request<void, UserStatusPayload>({
    url: `/users/${id}/status`,
    method: 'PUT',
    data: payload,
  })

export const updateUserAdminPermission = async (
  id: string,
  payload: UserAdminPermissionPayload,
): Promise<AdminConsumerUser> =>
  request<AdminConsumerUser, UserAdminPermissionPayload>({
    url: `/users/${id}/admin-permission`,
    method: 'PUT',
    data: payload,
  })

export const deleteAdminUser = async (id: string): Promise<void> =>
  request<void>({
    url: `/users/${id}`,
    method: 'DELETE',
  })

export const fetchAdminUserWalletLogs = async (id: string, params: { page?: number; pageSize?: number }): Promise<PageResult<AdminWalletLog>> =>
  request<PageResult<AdminWalletLog>>({
    url: `/users/${id}/wallet-logs`,
    method: 'GET',
    params,
  })

export const fetchAdminUserPointsLogs = async (id: string, params: { page?: number; pageSize?: number }): Promise<PageResult<AdminPointsLog>> =>
  request<PageResult<AdminPointsLog>>({
    url: `/users/${id}/points-logs`,
    method: 'GET',
    params,
  })
